// ABOUTME: Settings popover for the pivot table — heatmap and trend pills. Display mode is its own toggle in the header.
// ABOUTME: Triggered by a gear button in the pivot header; all options persist on currentReport.pivot. Options listed in the chart option's pivotHidden are not shown; 'settings' (or all sections) hides the gear entirely.
import { Box, ButtonBase, useTheme } from "@mui/material";
import { SettingsOutlined, KeyboardArrowDown } from "@mui/icons-material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../theme";
import Text from "../../../theme/text/Text";
import Subheading from "../../../theme/text/Subheading";
import PopoverModal from "../../../theme/Modal/PopoverModal";
import { DropdownBaseV2 } from "../../../theme/dropdowns/Dropdown";
import { getPivotView, pivotSettings, pivotSettingSections, pivotTrendVariants } from "../reportUtils";

const SettingRow = ({ label, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3, }}>
        <Text noBreak>{label}</Text>
        {children}
    </Box>
);

export const RhPivotSettings = ({ currentReport, setCurrentReport, chartOption, sx }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const view = getPivotView(currentReport);
    const hidden = chartOption?.pivotHidden || currentReport?.pivotHidden || [];

    const [anchorEl, setAnchorEl] = useState(null);

    // SETTINGS (or every section being hidden) hides the gear entirely — nothing left to configure.
    if (hidden.includes(pivotSettings.SETTINGS) || pivotSettingSections.every(s => hidden.includes(s))) return null;

    // Spread the whole existing pivot object (not just getPivotView's known keys) so per-mode
    // settings like sort/agg survive a heatmap/trends/mode change.
    const setView = (patch) => {
        setCurrentReport(prev => ({
            ...prev,
            pivot: { ...prev?.pivot, ...patch },
        }));
    };

    const trendOptions = [
        { value: pivotTrendVariants.LAST_YEAR, label: t('vs_last_year') },
        { value: pivotTrendVariants.PREVIOUS, label: t('vs_prev_period') },
        { value: pivotTrendVariants.CURRENT, label: t('current_period') },
    ];

    const isDark = theme.palette.mode === "dark";
    const accent = isDark ? colors.blue : colors.primary;

    const heatmapOptions = [
        { value: 'off', label: t('off') },
        { value: 'row', label: 'Per row' },
        { value: 'global', label: 'Global' },
    ];

    // Plain function (not a component) so DropdownBaseV2 keeps a stable type and its open state survives re-renders.
    const renderDropdown = (options, value, onChange) => {
        const idx = options.findIndex(o => o.value === value);
        return (
            <DropdownBaseV2
                options={options}
                displayName="label"
                selected={idx}
                onChange={(i) => onChange(options[i].value)}
                closeOnSelection
                disallowDeselection
            >
                {({ onClick }) => (
                    <ButtonBase
                        onClick={onClick}
                        sx={{
                            height: 32,
                            borderRadius: 5,
                            px: 2.5,
                            gap: 1,
                            background: isDark ? colors.grey[300] : colors.grey[100],
                            color: colors.txt['primary'],
                            fontWeight: 500,
                            fontSize: 14,
                        }}
                    >
                        {options[idx]?.label}
                        <Text light>
                        <KeyboardArrowDown fontSize="small" />
                        </Text>
                    </ButtonBase>
                )}
            </DropdownBaseV2>
        );
    };

    return (
        <>
            <ButtonBase
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    height: 40,
                    width: 40,
                    borderRadius: 5,
                    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                    color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400],
                    flexShrink: 0,
                    ...sx?.main,
                }}
            >
                <SettingsOutlined fontSize="small" />
            </ButtonBase>

            <PopoverModal
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                triangle={{ position: 'right', color: isDark ? colors.grey[200] : colors.grey[0] }}
            >
                <Box
                    sx={{
                        p: 3, pb:2,
                        minWidth: 360,
                        background: isDark ? colors.grey[200] : colors.grey[0]
                    }}
                >
                    <Subheading sx={{pb:2}}>{t('settings')}</Subheading>

                    <Box
                        sx={{
                            display:'flex',
                            flexDirection:"column",
                            gap:1.5,
                        }}
                    >
                        {!hidden.includes(pivotSettings.TRENDS) && (
                            <SettingRow label={t('trend_indicators')}>
                                <DropdownBaseV2
                                    options={trendOptions}
                                    displayName="label"
                                    valueKey="value"
                                    multiple
                                    allowSelectAll
                                    selected={view.trends}
                                    onChange={(vals) => setView({ trends: Array.isArray(vals) ? vals : [] })}
                                >
                                    {({ onClick }) => (
                                        <ButtonBase
                                            onClick={onClick}
                                            sx={{
                                                height: 32,
                                                borderRadius: 5,
                                                pl: 2,
                                                pr:1,
                                                gap: 1,
                                                background: isDark ? colors.grey[300] : colors.grey[100],
                                            }}
                                        >
                                            {view.trends.length === 0 ? 
                                                <Text>{t('off')}</Text>
                                            : 
                                                <>
                                                    <Text>{view.trends.length}</Text>
                                                    <Text light>{t("selected")}</Text>
                                                </>
                                            }
                                            <Text light>
                                                <KeyboardArrowDown fontSize="small" />
                                            </Text>
                                        </ButtonBase>
                                    )}
                                </DropdownBaseV2>
                            </SettingRow>
                        )}
                        {!hidden.includes(pivotSettings.HEATMAP) && (
                            <SettingRow label={t('heatmap')}>
                                {renderDropdown(heatmapOptions, view.heatmap, (value) => setView({ heatmap: value }))}
                            </SettingRow>
                        )}
                    </Box>
                </Box>
            </PopoverModal>
        </>
    );
};
